import { mat2d, type ReadonlyMat2d, type ReadonlyVec2, vec2 } from 'gl-matrix';

import { mat2dextended } from '../gl-matrix-extended/mat2d-extended';

import {
  type Box,
  boxTopLeft,
  boxBottomRight,
  boxSize,
  boxCenter,
  type ReadonlyBox,
} from './box';

/**
 * Returns a mat2d that transforms a boxes local coord space
 * (from the top left of the box) into global coord space.
 * This does not transform the scale (1px local = 1px global)
 *
 * e.g. if a box is {left: 2, right: 4, top: 4, bottom: 8},
 * using this mat2d to transform the local point {x: 0, y: 1}
 * will yield {x: 2, y: 5} in global space
 *
 * Use vec2.transformMat2d on the matrix to transform a point:
 *
 * let global = vec2.transformMat2d(vec2.create(), local, boxToMat2d(box));
 *
 * The matrix can also be inverted to convert from global to local:
 *
 * let invertedMatrix = mat2d.invert(mat2d.create(), boxToMat2d(box));
 *
 * @params box: Box
 *
 * @returns mat2d
 */

export function boxToMat2d(box: ReadonlyBox): mat2d {
  const m = mat2d.create();
  return mat2d.translate(m, m, boxTopLeft(box));
}

/**
 * Returns a mat2d that transforms a boxes normalized local coord space
 * (from the top left of the box) into global coord space
 * Normalized local space is (0,0) at the top left, (1,0) at the top right
 * and (1,1) at the bottom right.
 *
 * e.g. if a box is {left: 2, right: 4, top: 4, bottom: 8},
 * using this mat2d to transform the local point {x: 0.5, y: 0.5}
 * will yield {x: 3, y: 6} in global space
 *
 * Use vec2.transformMat2d on the matrix to transform a point:
 *
 * let global = vec2.transformMat2d(vec2.create(), local, boxToMat2dNormalized(box));
 *
 * The matrix can also be inverted to convert from global to local:
 *
 * let invertedMatrix = mat2d.invert(mat2d.create(), boxToMat2dNormalized(box));
 *
 * @params box: Box
 * @returns mat2d
 */

export function boxToMat2dNormalized(box: ReadonlyBox): mat2d {
  const m = boxToMat2d(box);
  return mat2d.scale(m, m, boxSize(box));
}

/**
 * Returns a mat2d that transforms a rotated boxes local coord space
 * (from the top left of the box) into global coord space,
 * This does not transform the scale (1px local = 1px global)
 *
 * Use vec2.transformMat2d on the matrix to transform a point:
 *
 * let global = vec2.transformMat2d(vec2.create(), local, boxToMat2dRotated(box));
 *
 * The matrix can also be inverted to convert from global to local:
 *
 * let invertedMatrix = mat2d.invert(mat2d.create(), boxToMat2dRotated(box));
 *
 * @params box: Box
 * @params rotation: optional number of radians to rotate the box clockwise, defaults to 0
 * @params rotationOrigin: optional point to rotate around, defaults to center of box
 *
 * @returns mat2d
 */

const rotationOffsetFlyweight = vec2.create();

export function boxToMat2dRotated(
  box: ReadonlyBox,
  rotation = 0,
  rotationOrigin: ReadonlyVec2 = boxCenter(box)
): mat2d {
  const m = mat2d.create();
  const topLeft = boxTopLeft(box);

  // translate according to box position
  mat2d.translate(m, m, topLeft);

  // rotate around rotationOrigin
  vec2.sub(rotationOffsetFlyweight, rotationOrigin, topLeft);
  mat2dextended.rotateAround(m, m, rotation, rotationOffsetFlyweight);

  return m;
}

/**
 * Returns a mat2d that transforms a rotated boxes normalized local coord space
 * (from the top left of the box) into global coord space
 * Normalized local space is (0,0) at the top left, (1,0) at the top right
 * and (1,1) at the bottom right.
 *
 * e.g. if a box is {left: 2, right: 4, top: 4, bottom: 8},
 * using this mat2d to transform the local point {x: 0.5, y: 0.5}
 * will yield {x: 3, y: 6} in global space
 *
 * Use vec2.transformMat2d on the matrix to transform a point:
 *
 * let global = vec2.transformMat2d(vec2.create(), local, boxToMat2dRotatedNormalized(box));
 *
 * The matrix can also be inverted to convert from global to local:
 *
 * let invertedMatrix = mat2d.invert(mat2d.create(), boxToMat2dRotatedNormalized(box));
 *
 * @params box: Box
 * @params rotation: optional number of radians to rotate the box clockwise, defaults to 0
 * @params rotationOrigin: optional point to rotate around, defaults to center of box
 * @returns mat2d
 */

export function boxToMat2dRotatedNormalized(
  box: ReadonlyBox,
  rotation?: number,
  rotationOrigin?: ReadonlyVec2
): mat2d {
  const m = boxToMat2dRotated(box, rotation, rotationOrigin);
  return mat2d.scale(m, m, boxSize(box));
}

/**
 * Transforms a point from the boxes local coord space
 * (from the top left of the box) into global coord space.
 * This does not transform the scale (1px local = 1px global)
 *
 * e.g. if a box is {left: 2, right: 4, top: 4, bottom: 8},
 * providing the local point {x: 0, y: 1}
 * will yield {x: 2, y: 5} in global space
 *
 * This is a convenience method for readability / discoverability / people
 * who don't like using matrices. But if you need to transform heaps of points
 * through the same box, please use boxToMat2d and transformMat2d directly
 * so you can avoid recreating the matrix for every point.
 *
 * @params box: Box
 * @params point: vec2 - the point in the boxes local coordinate space
 * @returns vec2 - the point in global coordinate space
 */

export function boxLocalToGlobal(box: ReadonlyBox, point: ReadonlyVec2): vec2 {
  return vec2.transformMat2d(vec2.create(), point, boxToMat2d(box));
}

/**
 * The inverse of boxLocalToGlobal()
 *
 * @params box: Box
 * @params point: vec2 - the point in the boxes local coordinate space
 * @returns vec2 - the point in global coordinate space
 */

export function boxGlobalToLocal(box: ReadonlyBox, point: ReadonlyVec2): vec2 {
  const m = boxToMat2d(box);
  mat2d.invert(m, m);
  return vec2.transformMat2d(vec2.create(), point, m);
}

/**
 * Transforms a point from the boxes normalized local coord space
 * (from the top left of the box) into global coord space.
 * This does not transform the scale (1px local = 1px global)
 *
 * e.g. if a box is {left: 2, right: 4, top: 4, bottom: 8},
 * providing the local point {x: 0.5, y: 0.5}
 * will yield {x: 3, y: 6} in global space
 *
 * This is a convenience method for readability / discoverability / people
 * who don't like using matrices. But if you need to transform heaps of points
 * through the same box, please use boxToMat2dNormalized and transformMat2d directly
 * so you can avoid recreating the matrix for every point.
 *
 * @params box: Box
 * @params point: vec2 - the point in the boxes local normalized coordinate space
 * @returns vec2 - the point in global coordinate space
 */

export function boxNormalizedLocalToGlobal(box: ReadonlyBox, point: ReadonlyVec2): vec2 {
  return vec2.transformMat2d(vec2.create(), point, boxToMat2dNormalized(box));
}

/**
 * The inverse of boxNormalizedLocalToGlobal()
 *
 * @params box: Box
 * @params point: vec2 - the point in the boxes local coordinate space
 * @returns vec2 - the point in global coordinate space
 */

export function boxGlobalToNormalizedLocal(box: ReadonlyBox, point: ReadonlyVec2): vec2 {
  const m = boxToMat2dNormalized(box);
  mat2d.invert(m, m);
  return vec2.transformMat2d(vec2.create(), point, m);
}

/**
 * Translates a box by a specific set of coords
 * @param box
 * @param translation
 * @returns
 */
export function boxTranslate(box: ReadonlyBox, translation: ReadonlyVec2) {
  return {
    left: box.left + translation[0],
    right: box.right + translation[0],
    bottom: box.bottom + translation[1],
    top: box.top + translation[1],
  };
}

/**
 * Scales a box according to a scalar
 *
 * @params box: Box - the box to scale
 * @params scalar: number - the amount to scale the box by
 * @returns Box - the scaled box
 */

export function boxScale(box: ReadonlyBox, scalar: number): Box {
  return {
    left: box.left * scalar,
    top: box.top * scalar,
    right: box.right * scalar,
    bottom: box.bottom * scalar,
  };
}

/**
 * Scales all sides of a box uniformly by a percentage, centered at the center of the box.
 */
export function scaleBoxAroundCenter(box: ReadonlyBox, percent: number): Box | null {
  if (percent <= 0) {
    return null;
  }

  const [x, y] = boxCenter(box);
  const [width, height] = boxSize(box);

  const scaledWidth = width * percent;
  const scaledHeight = height * percent;

  const halfOfScaledWidth = scaledWidth / 2;
  const halfOfScaledHeight = scaledHeight / 2;

  const newLeft = x - halfOfScaledWidth;
  const newRight = x + halfOfScaledWidth;
  const newTop = y - halfOfScaledHeight;
  const newBottom = y + halfOfScaledHeight;

  return {
    left: newLeft,
    right: newRight,
    top: newTop,
    bottom: newBottom,
  };
}

/**
 * Transforms a box according to a matrix (mat2d)
 *
 * @params box: Box - the box to transform
 * @params matrix: mat2d - the matrix to transform the box with
 * @returns Box - the transformed box
 */

export function boxTransform(box: ReadonlyBox, matrix: ReadonlyMat2d): Box {
  const topLeft = boxTopLeft(box);
  const bottomRight = boxBottomRight(box);

  vec2.transformMat2d(topLeft, topLeft, matrix);
  vec2.transformMat2d(bottomRight, bottomRight, matrix);

  return {
    left: topLeft[0],
    top: topLeft[1],
    right: bottomRight[0],
    bottom: bottomRight[1],
  };
}

/**
 * Premade positions on a box
 */

export const boxAnchorPoint = {
  TOP_LEFT: vec2.fromValues(0, 0),
  TOP: vec2.fromValues(0.5, 0),
  TOP_RIGHT: vec2.fromValues(1, 0),
  LEFT: vec2.fromValues(0, 0.5),
  CENTER: vec2.fromValues(0.5, 0.5),
  RIGHT: vec2.fromValues(1, 0.5),
  BOTTOM_LEFT: vec2.fromValues(0, 1),
  BOTTOM: vec2.fromValues(0.5, 1),
  BOTTOM_RIGHT: vec2.fromValues(1, 1),
};

/**
 * Sets the position of a box
 *
 * @params box: Box - the box to re-position
 * @params pos: vec2 - the new position of the box
 * @params anchor: vec2 - the anchor point on the box to move to the position. This defaults to boxAnchorPoint.TOP_LEFT i.e. (0,0).
 * This can be changed to any other anchor point, so an anchor of boxAnchorPoint.CENTER will move the center of the box to `pos`.
 * @returns Box - the re-positioned box
 */

export function boxSetPosition(
  box: ReadonlyBox,
  pos: ReadonlyVec2,
  anchor: ReadonlyVec2 = boxAnchorPoint.TOP_LEFT
): Box {
  const anchorPos = boxNormalizedLocalToGlobal(box, anchor);
  const translation = vec2.sub(vec2.create(), pos, anchorPos);
  return boxTranslate(box, translation);
}

/**
 * Sets the size of a box
 *
 * @params box: Box - the box to re-size
 * @params size: vec2 - the new size of the box
 * @params around: vec2 - the point in which to resize the box around. This defaults to boxAnchorPoint.TOP_LEFT i.e. (0,0).
 * This can be changed to any other anchor point, so an anchor of boxAnchorPoint.CENTER will scale around the center of the box.
 * @returns Box - the re-sized box
 */

export function boxSetSize(
  box: Box,
  size: ReadonlyVec2,
  anchor: ReadonlyVec2 = boxAnchorPoint.TOP_LEFT
): Box {
  const topLeft = boxTopLeft(box);
  const bottomRight = boxBottomRight(box);
  const sizePrev = boxSize(box);

  const sizeDiff = vec2.sub(vec2.create(), size, sizePrev);
  const anchorInverse = vec2.sub(vec2.create(), vec2.fromValues(1, 1), anchor);

  const topLeftMovement = vec2.mul(vec2.create(), sizeDiff, anchor);
  const bottomRightMovement = vec2.mul(vec2.create(), sizeDiff, anchorInverse);
  vec2.sub(topLeft, topLeft, topLeftMovement);
  vec2.add(bottomRight, bottomRight, bottomRightMovement);

  return {
    left: topLeft[0],
    top: topLeft[1],
    right: bottomRight[0],
    bottom: bottomRight[1],
  };
}
